import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowRight, Upload, Palette, Download, Check, Zap, Clock, Image, Star, Sparkles, Layout } from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard/mockup');
    } else {
      navigate('/auth?mode=register');
    }
  };

  const scrollToExamples = () => {
    document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Style Collections - 5 curated bundles
  const styleCollections = [
    {
      name: 'Coastal Calm',
      description: 'Soft beach vibes with natural light',
      images: [
        '/images/styles/coastal/coastal-1.jpg',
        '/images/styles/coastal/coastal-2.jpg',
        '/images/styles/coastal/coastal-3.jpg'
      ]
    },
    {
      name: 'Modern Minimal',
      description: 'Clean lines and white spaces',
      images: [
        '/images/styles/minimal/minimal-1.jpg',
        '/images/styles/minimal/minimal-2.jpg',
        '/images/styles/minimal/minimal-3.jpg'
      ]
    },
    {
      name: 'Cozy Home',
      description: 'Warm textures and soft lighting',
      images: [
        '/images/styles/cozy/cozy-1.jpg',
        '/images/styles/cozy/cozy-2.jpg',
        '/images/styles/cozy/cozy-3.jpg'
      ]
    },
    {
      name: 'Boho Natural',
      description: 'Earthy tones with rattan and plants',
      images: [
        '/images/styles/boho/boho-1.jpg',
        '/images/styles/boho/boho-2.jpg',
        '/images/styles/boho/boho-3.jpg'
      ]
    },
    {
      name: 'Bright Lifestyle',
      description: 'Bold colors and fun energy',
      images: [
        '/images/styles/bright/bright-1.jpg',
        '/images/styles/bright/bright-2.jpg',
        '/images/styles/bright/bright-3.jpg'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-[#E5E7EB]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#4DA1FF] flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="font-semibold text-lg text-[#111827]">MockupPro</span>
          </Link>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <button 
                onClick={() => navigate('/dashboard')} 
                className="h-10 px-5 bg-[#4DA1FF] text-white font-medium text-[15px] rounded-[10px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button 
                  onClick={() => navigate('/auth')} 
                  className="h-10 px-5 text-[#6B7280] font-medium text-[15px] hover:text-[#111827] transition-colors"
                >
                  Log in
                </button>
                <button 
                  onClick={handleGetStarted} 
                  className="h-10 px-5 bg-[#4DA1FF] text-white font-medium text-[15px] rounded-[10px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  Get started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              <h1 className="text-[40px] sm:text-[48px] lg:text-[54px] font-bold leading-[1.1] tracking-[-0.02em] mb-6">
                Create Etsy Listing Photos That Actually Sell
              </h1>
              
              <p className="text-[18px] lg:text-[20px] text-[#6B7280] leading-relaxed mb-8 max-w-[480px]">
                Upload your design. Choose a style. Download a polished mockup in seconds.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <button 
                  onClick={handleGetStarted}
                  className="h-12 px-7 bg-[#4DA1FF] text-white font-semibold text-[16px] rounded-[10px] shadow-lg shadow-blue-100 hover:shadow-xl hover:shadow-blue-200 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
                >
                  Make My Listing Photo
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={scrollToExamples}
                  className="h-12 px-7 border border-[#E5E7EB] text-[#111827] font-semibold text-[16px] rounded-[10px] hover:bg-[#F8F9FB] transition-all"
                >
                  See Examples
                </button>
              </div>
              
              <p className="text-[15px] text-[#6B7280] border-l-2 border-[#4DA1FF] pl-4">
                For Etsy sellers who want better listing photos without Photoshop
              </p>
            </div>
            
            {/* Right Content - HERO IMAGE: Sticker on desk */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl shadow-gray-300/60 overflow-hidden border border-[#E5E7EB]/50">
                  <img 
                    src="/images/hero-sticker.jpg" 
                    alt="Cute camera sticker on desk - product mockup"
                    className="w-full max-w-[520px] h-auto"
                  />
                </div>
                {/* Soft shadow underneath */}
                <div className="absolute -bottom-4 left-8 right-8 h-8 bg-gray-200/60 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-5 border-y border-[#E5E7EB] bg-[#F8F9FB]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {[
              { icon: <Sparkles className="w-4 h-4" />, text: 'Built for Etsy sellers' },
              { icon: <Zap className="w-4 h-4" />, text: 'No design skills needed' },
              { icon: <Clock className="w-4 h-4" />, text: 'Download instantly' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#6B7280] text-[14px]">
                <span className="text-[#4DA1FF]">{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER - UPGRADED */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[32px] lg:text-[42px] font-bold tracking-[-0.02em] mb-4">
              See the transformation
            </h2>
            <p className="text-[18px] text-[#6B7280] max-w-[500px] mx-auto">
              Your design becomes a professional listing photo
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 lg:gap-6 max-w-[1000px] mx-auto">
            {/* Before - Plain design */}
            <div className="flex-1 w-full">
              <div className="h-full bg-[#F8F9FB] rounded-2xl p-6 lg:p-8 border border-[#E5E7EB]">
                <p className="text-[12px] font-medium text-[#9CA3AF] uppercase tracking-wider mb-4">Before</p>
                <div className="aspect-square bg-white rounded-xl flex items-center justify-center border-2 border-dashed border-[#D1D5DB]">
                  <div className="text-center p-8">
                    <div className="w-28 h-28 mx-auto rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg">
                      <svg className="w-14 h-14 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0-2a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm6.5-.25a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 4c2.5 0 2.8.01 3.8.05.9.04 1.4.2 1.7.33.4.17.7.37 1.1.67.3.3.5.6.67 1.1.13.3.29.8.33 1.7.04 1 .05 1.3.05 3.8s-.01 2.8-.05 3.8c-.04.9-.2 1.4-.33 1.7-.17.4-.37.7-.67 1.1-.3.3-.6.5-1.1.67-.3.13-.8.29-1.7.33-1 .04-1.3.05-3.8.05s-2.8-.01-3.8-.05c-.9-.04-1.4-.2-1.7-.33-.4-.17-.7-.37-1.1-.67-.3-.3-.5-.6-.67-1.1-.13-.3-.29-.8-.33-1.7-.04-1-.05-1.3-.05-3.8s.01-2.8.05-3.8c.04-.9.2-1.4.33-1.7.17-.4.37-.7.67-1.1.3-.3.6-.5 1.1-.67.3-.13.8-.29 1.7-.33 1-.04 1.3-.05 3.8-.05z"/>
                      </svg>
                    </div>
                    <p className="text-[14px] text-[#9CA3AF]">Raw design file</p>
                    <p className="text-[12px] text-[#D1D5DB]">camera.png</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="flex items-center justify-center py-4 md:py-0">
              <div className="w-12 h-12 rounded-full bg-[#4DA1FF] flex items-center justify-center shadow-lg shadow-blue-200">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
            </div>
            
            {/* After - Professional mockup - 15% larger */}
            <div className="flex-1 w-full md:scale-[1.08] origin-center">
              <div className="h-full bg-white rounded-2xl p-6 lg:p-8 border-2 border-[#4DA1FF]/30 shadow-2xl shadow-blue-100/80">
                <p className="text-[12px] font-bold text-[#4DA1FF] uppercase tracking-wider mb-4">After</p>
                <div className="aspect-square rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/hero-sticker.jpg" 
                    alt="Professional sticker mockup"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-center mt-12 text-[16px] text-[#6B7280]">
            <span className="font-semibold text-[#111827]">Better photos = more clicks = more sales.</span> It's that simple.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32 bg-[#F8F9FB]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[32px] lg:text-[40px] font-bold tracking-[-0.02em] mb-4">
              How it works
            </h2>
            <p className="text-[18px] text-[#6B7280]">
              Three steps to professional listing photos
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Upload className="w-6 h-6" />, title: 'Upload your design', desc: 'Drop your PNG with transparent background' },
              { icon: <Palette className="w-6 h-6" />, title: 'Choose product and style', desc: 'Pick from stickers, mugs, or wall art' },
              { icon: <Download className="w-6 h-6" />, title: 'Download your mockup', desc: 'Get a polished image ready for Etsy' }
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-[#E5E7EB] shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-5 text-[#4DA1FF]">
                  {step.icon}
                </div>
                <h3 className="text-[18px] font-semibold mb-2">{step.title}</h3>
                <p className="text-[15px] text-[#6B7280] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Types */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[32px] lg:text-[40px] font-bold tracking-[-0.02em] mb-4">
              Mockups for every product
            </h2>
            <p className="text-[18px] text-[#6B7280]">
              Choose from stickers, mugs, and wall art templates
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Sticker Mockups', desc: 'Laptop, notebook, water bottle scenes', image: '/images/sticker-van.jpg' },
              { title: 'Mug Mockups', desc: 'Studio, kitchen, cozy lifestyle shots', image: '/images/hero-cozy-mug.jpg' },
              { title: 'Wall Art Mockups', desc: 'Living room, bedroom, office frames', image: '/images/wall-art-living.jpg' }
            ].map((product, i) => (
              <div
                key={i}
                onClick={handleGetStarted}
                className="group bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-100 hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-[18px] font-semibold mb-1">{product.title}</h3>
                  <p className="text-[14px] text-[#6B7280]">{product.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR STYLE - 5 Curated Collections */}
      <section id="examples" className="py-24 lg:py-32 bg-[#F8F9FB]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[32px] lg:text-[42px] font-bold tracking-[-0.02em] mb-4">
              Choose your style
            </h2>
            <p className="text-[18px] text-[#6B7280] max-w-[520px] mx-auto">
              Premium mockup collections designed to match your brand aesthetic
            </p>
          </div>
          
          {/* 5 Style Collection Cards */}
          <div className="space-y-6">
            {styleCollections.map((style, idx) => (
              <div
                key={idx}
                onClick={handleGetStarted}
                className="group bg-white rounded-2xl border border-[#E5E7EB] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer overflow-hidden"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Style Info */}
                  <div className="md:w-[240px] p-6 md:p-8 flex flex-col justify-center border-b md:border-b-0 md:border-r border-[#E5E7EB]">
                    <h3 className="text-[20px] lg:text-[22px] font-semibold mb-2 text-[#111827]">
                      {style.name}
                    </h3>
                    <p className="text-[14px] lg:text-[15px] text-[#6B7280] leading-relaxed">
                      {style.description}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-[#4DA1FF] text-[14px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>Use this style</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                  
                  {/* Style Images */}
                  <div className="flex-1 p-4 md:p-5">
                    <div className="grid grid-cols-3 gap-3 md:gap-4">
                      {style.images.map((img, imgIdx) => (
                        <div
                          key={imgIdx}
                          className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F8F9FB]"
                        >
                          <img
                            src={img}
                            alt={`${style.name} style ${imgIdx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-14">
            <button 
              onClick={handleGetStarted}
              className="h-12 px-8 bg-[#4DA1FF] text-white font-semibold text-[16px] rounded-[10px] shadow-lg shadow-blue-100 hover:shadow-xl hover:shadow-blue-200 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
            >
              Start Creating
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[32px] lg:text-[40px] font-bold tracking-[-0.02em]">
              Why sellers choose MockupPro
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Image className="w-5 h-5" />, title: 'Realistic mockups', desc: 'Professional product photography quality' },
              { icon: <Layout className="w-5 h-5" />, title: 'Clean layouts', desc: 'High-converting listing images' },
              { icon: <Zap className="w-5 h-5" />, title: 'Fast workflow', desc: 'Generate mockups in seconds' },
              { icon: <Star className="w-5 h-5" />, title: 'Built for Etsy', desc: 'Designed for print-on-demand sellers' }
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-[#E5E7EB] shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center mb-4 text-[#4DA1FF]">
                  {feature.icon}
                </div>
                <h3 className="text-[16px] font-semibold mb-2">{feature.title}</h3>
                <p className="text-[14px] text-[#6B7280]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 lg:py-32 bg-[#111827]">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h2 className="text-[32px] lg:text-[42px] font-bold text-white tracking-[-0.02em] mb-4">
            Ready to upgrade your listings?
          </h2>
          <p className="text-[18px] text-gray-400 mb-10">
            Join Etsy sellers who are creating listing photos that actually convert.
          </p>
          <button 
            onClick={handleGetStarted}
            className="h-14 px-10 bg-[#4DA1FF] text-white font-semibold text-[17px] rounded-[10px] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
          >
            Make My Listing Photo
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-[#4DA1FF] flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">M</span>
            </div>
            <span className="font-medium text-[15px]">MockupPro</span>
          </div>
          <div className="flex items-center gap-6 text-[14px] text-[#6B7280]">
            <Link to="/terms" className="hover:text-[#111827] transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-[#111827] transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-[#111827] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
